import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout';
import { useToast } from '../../hooks';
import { getLead } from '../../api/leads';
import {
  getDecisionEngineOutput,
  getExternalResearch,
  getProposalReviewStatus,
  proceedFromProposalReview,
  declineFromProposalReview,
  scheduleFollowupFromProposalReview,
  triggerDecisionEngine,
} from '../../api/proposals';
import {
  ProposalStatusBadge,
  DecisionEngineCard,
  ExternalResearchCard,
} from '../../components/proposal';

const WORKER_LIST = [
  { type: 'origination', label: 'Origination Intelligence' },
  { type: 'regulatory_structuring', label: 'Regulatory & Structuring' },
  { type: 'technology', label: 'Technology Intelligence' },
  { type: 'distribution', label: 'Distribution Intelligence' },
];

/**
 * Unified Proposal Review Page
 * Combines Decision Engine results + External Research findings
 * into a single human review gate before proposal generation.
 */
const DecisionEngineReviewPage = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [lead, setLead] = useState(null);
  const [deOutput, setDeOutput] = useState(null);
  const [research, setResearch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [workerModes, setWorkerModes] = useState(
    WORKER_LIST.reduce((acc, w) => ({ ...acc, [w.type]: true }), {})
  );
  const [declineComment, setDeclineComment] = useState('');
  const [showDecline, setShowDecline] = useState(false);
  const [followupComment, setFollowupComment] = useState('');
  const [showFollowup, setShowFollowup] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [leadRes, deRes, erRes] = await Promise.allSettled([
        getLead(leadId),
        getDecisionEngineOutput(leadId),
        getExternalResearch(leadId),
      ]);

      if (leadRes.status === 'fulfilled') setLead(leadRes.value?.data);
      if (deRes.status === 'fulfilled') setDeOutput(deRes.value?.data);
      if (erRes.status === 'fulfilled') setResearch(erRes.value?.data);

      // Check if still processing
      const de = deRes.status === 'fulfilled' ? deRes.value?.data : null;
      const er = erRes.status === 'fulfilled' ? erRes.value?.data : null;

      const deProcessing = de && ['queued', 'running'].includes(de.status);
      const erProcessing = er && ['queued', 'running'].includes(er.status);
      const deDoneNoEr = de?.status === 'completed' && !er;

      if (deProcessing || erProcessing || deDoneNoEr) {
        setIsProcessing(true);
        setProcessingMessage(
          deProcessing ? 'Decision Engine analyzing lead data...'
            : deDoneNoEr ? 'Starting external market research...'
            : 'External research gathering market intelligence...'
        );
      } else {
        setIsProcessing(false);
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        showToast('Failed to load review data', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [leadId, showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Polling while processing
  useEffect(() => {
    if (!isProcessing) return;
    const interval = setInterval(async () => {
      try {
        const res = await getProposalReviewStatus(leadId);
        const data = res.data;
        if (data.both_complete || (!data.is_processing && !data.should_poll)) {
          setIsProcessing(false);
          fetchData();
        } else {
          setProcessingMessage(data.message || 'Processing...');
        }
      } catch {
        // ignore polling errors
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [isProcessing, leadId, fetchData]);

  const handleProceed = async () => {
    if (!deOutput || !research) return;
    setActionLoading(true);
    try {
      const modes = WORKER_LIST.map(w => ({
        worker_type: w.type,
        mode: workerModes[w.type] ? 'required' : 'optional',
      }));
      await proceedFromProposalReview(leadId, { worker_modes: modes });
      showToast('Proposal generation started!', 'success');
      navigate(`/leads/${leadId}/proposals/generating`);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to proceed', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    setActionLoading(true);
    try {
      await declineFromProposalReview(leadId, { comment: declineComment });
      showToast('Pipeline declined.', 'success');
      navigate(`/leads/${leadId}?tab=proposals`);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to decline', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleScheduleFollowup = async () => {
    setActionLoading(true);
    try {
      await scheduleFollowupFromProposalReview(leadId, { comment: followupComment });
      showToast('Status updated to Schedule Follow-up', 'success');
      navigate(`/leads/${leadId}?tab=meetings`);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to schedule follow-up', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRetrigger = async () => {
    setActionLoading(true);
    try {
      await triggerDecisionEngine(leadId);
      showToast('Decision Engine re-triggered', 'success');
      setIsProcessing(true);
      setProcessingMessage('Decision Engine analyzing lead data...');
      setLoading(true);
      setTimeout(fetchData, 2000);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to trigger', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const bothComplete = deOutput?.status === 'completed' && research?.status === 'completed';
  const humanDecisionMade = deOutput?.human_decision || research?.human_decision;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="w-full py-6 px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <button
              onClick={() => navigate(`/leads/${leadId}?tab=proposals`)}
              className="text-sm text-gray-500 hover:text-gray-700 mb-1 inline-flex items-center"
            >
              &larr; Back to Lead
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Proposal Review</h1>
            {lead && (
              <p className="text-gray-600 mt-1">{lead.name} - {lead.company}</p>
            )}
          </div>
          {deOutput && (
            <ProposalStatusBadge status={deOutput.result || deOutput.status} size="lg" />
          )}
        </div>

        {/* Processing State */}
        {isProcessing && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
            <div className="flex items-center gap-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 flex-shrink-0"></div>
              <div>
                <h3 className="text-lg font-medium text-gray-700">Analysis in Progress</h3>
                <p className="text-sm text-gray-500 mt-1">{processingMessage}</p>
                <div className="flex gap-4 mt-3">
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`w-2 h-2 rounded-full ${deOutput?.status === 'completed' ? 'bg-green-500' : deOutput ? 'bg-blue-500 animate-pulse' : 'bg-gray-300'}`} />
                    <span className="text-gray-500 dark:text-gray-400">Decision Engine</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`w-2 h-2 rounded-full ${research?.status === 'completed' ? 'bg-green-500' : research ? 'bg-blue-500 animate-pulse' : 'bg-gray-300'}`} />
                    <span className="text-gray-500 dark:text-gray-400">External Research</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* No Data State */}
        {!deOutput && !isProcessing && (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Analysis Available</h3>
            <p className="text-gray-500 mb-4">The AI analysis has not been run for this lead yet.</p>
            <button
              onClick={handleRetrigger}
              disabled={actionLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {actionLoading ? 'Triggering...' : 'Run Analysis'}
            </button>
          </div>
        )}

        {/* Combined Results */}
        {deOutput && deOutput.status === 'completed' && (
          <div className="space-y-6">
            {/* ============================================================ */}
            {/* AI ANALYSIS SECTION */}
            {/* ============================================================ */}
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="bg-blue-50 border-b border-blue-100 px-5 py-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <h2 className="text-lg font-bold text-blue-900">Analysis</h2>
              </div>
              <div className="p-5 space-y-6">

            {/* SECTION 1: Decision Engine Results */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <svg className="w-4 h-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900">Decision Engine Analysis</h2>
              </div>

              {/* Overall Assessment */}
              {deOutput.overall_assessment && (
                <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Executive Summary</h3>
                  <p className="text-gray-700 leading-relaxed">{deOutput.overall_assessment}</p>
                </div>
              )}

              {/* Key Findings */}
              {deOutput.key_findings && deOutput.key_findings.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Key Findings</h3>
                  <div className="space-y-2">
                    {deOutput.key_findings.map((finding, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <span className={`mt-0.5 flex-shrink-0 w-2 h-2 rounded-full ${
                          finding.impact === 'positive' ? 'bg-green-500' :
                          finding.impact === 'negative' ? 'bg-red-500' : 'bg-gray-400'
                        }`} />
                        <div>
                          <span className="text-sm font-medium text-gray-800">{finding.finding}</span>
                          {finding.detail && (
                            <p className="text-sm text-gray-500">{finding.detail}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Validation Checks */}
              {deOutput.checks && deOutput.checks.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Validation Checks</h3>
                  {deOutput.checks.map((check, idx) => (
                    <DecisionEngineCard key={idx} check={check} />
                  ))}
                </div>
              )}

              {/* Risk Flags */}
              {deOutput.risk_flags && deOutput.risk_flags.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Risk Flags</h3>
                  <div className="space-y-2">
                    {deOutput.risk_flags.map((flag, idx) => (
                      <div key={idx} className={`rounded-lg p-4 border ${
                        flag.severity === 'high' ? 'bg-red-50 border-red-200' :
                        flag.severity === 'medium' ? 'bg-yellow-50 border-yellow-200' :
                        'bg-blue-50 border-blue-200'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-semibold uppercase ${
                            flag.severity === 'high' ? 'text-red-600' :
                            flag.severity === 'medium' ? 'text-yellow-600' : 'text-blue-600'
                          }`}>
                            {flag.severity}
                          </span>
                          <span className="text-sm font-medium text-gray-800">{flag.flag}</span>
                        </div>
                        {flag.detail && <p className="text-sm text-gray-600">{flag.detail}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ============================================================ */}
            {/* SECTION 2: External Research */}
            {/* ============================================================ */}
            {research && research.status === 'completed' && (
              <div>
                <div className="flex items-center gap-2 mb-4 pt-4 border-t border-gray-200">
                  <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center">
                    <svg className="w-4 h-4 text-cyan-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">External Market Intelligence</h2>
                </div>

                {/* Research Summary */}
                {research.summary && (
                  <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Research Summary</h3>
                    <p className="text-gray-700 leading-relaxed">{research.summary}</p>
                  </div>
                )}

                {/* Research Findings */}
                {research.research_results && research.research_results.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Research Findings</h3>
                    {research.research_results.map((item, idx) => (
                      <ExternalResearchCard key={idx} research={item} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Still waiting for external research */}
            {research && ['queued', 'running'].includes(research.status) && (
              <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mx-auto mb-4"></div>
                <h3 className="text-lg font-medium text-gray-700">External Research in Progress</h3>
                <p className="text-sm text-gray-500 mt-1">Gathering market intelligence... This will appear here shortly.</p>
              </div>
            )}

              </div>
            </div>

            {/* ============================================================ */}
            {/* HUMAN REVIEW SECTION */}
            {/* ============================================================ */}
            {bothComplete && !humanDecisionMade && (
              <div className="rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                <div className="bg-amber-50 border-b border-amber-100 px-5 py-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <h2 className="text-lg font-bold text-amber-900">Human Review</h2>
                </div>
                <div className="p-5 space-y-5">

                {/* Worker Selection */}
                <div className="bg-white rounded-lg border border-gray-200 p-5 pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <svg className="w-4 h-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <h2 className="text-lg font-bold text-gray-900">AI Worker Selection</h2>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">
                    All workers will run. Checked workers produce required sections; unchecked produce supplementary analysis.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {WORKER_LIST.map(worker => (
                      <label key={worker.type} className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition">
                        <input
                          type="checkbox"
                          checked={workerModes[worker.type]}
                          onChange={() => setWorkerModes(prev => ({
                            ...prev,
                            [worker.type]: !prev[worker.type],
                          }))}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-0"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-700">{worker.label}</span>
                          <span className="text-xs text-gray-400 ml-2">
                            ({workerModes[worker.type] ? 'Required' : 'Optional'})
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-4 pt-4 border-t border-gray-200">
                  {!showDecline && !showFollowup ? (
                    <div className="flex gap-4">
                      <button
                        onClick={handleProceed}
                        disabled={actionLoading}
                        className="flex-1 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        {actionLoading ? 'Starting Proposal Generation...' : 'Generate Proposal'}
                      </button>
                      <button
                        onClick={() => { setShowFollowup(true); setShowDecline(false); }}
                        className="px-6 py-3 bg-white border border-orange-300 text-orange-600 rounded-lg font-medium hover:bg-orange-50 transition-colors"
                      >
                        Schedule Follow-up
                      </button>
                      <button
                        onClick={() => { setShowDecline(true); setShowFollowup(false); }}
                        className="px-6 py-3 bg-white border border-red-300 text-red-600 rounded-lg font-medium hover:bg-red-50 transition-colors"
                      >
                        Decline
                      </button>
                    </div>
                  ) : showFollowup ? (
                    <div className="w-full space-y-3">
                      <textarea
                        value={followupComment}
                        onChange={(e) => setFollowupComment(e.target.value)}
                        placeholder="Follow-up comment (e.g., topics to discuss, required documents)..."
                        rows={2}
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-orange-500 focus:ring-0 text-sm"
                      />
                      <div className="flex gap-3">
                        <button
                          onClick={handleScheduleFollowup}
                          disabled={actionLoading}
                          className="flex-1 py-2.5 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50"
                        >
                          {actionLoading ? 'Scheduling...' : 'Confirm & Schedule Meeting'}
                        </button>
                        <button
                          onClick={() => setShowFollowup(false)}
                          className="px-6 py-2.5 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full space-y-3">
                      <textarea
                        value={declineComment}
                        onChange={(e) => setDeclineComment(e.target.value)}
                        placeholder="Explain why this lead is being declined (minimum 10 characters)..."
                        rows={2}
                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-red-500 focus:ring-0 text-sm"
                      />
                      <p className="text-xs text-gray-400">
                        {declineComment.length < 10
                          ? `${10 - declineComment.length} more character${10 - declineComment.length === 1 ? '' : 's'} required`
                          : `${declineComment.length} characters`
                        }
                      </p>
                      <div className="flex gap-3">
                        <button
                          onClick={handleDecline}
                          disabled={actionLoading || declineComment.length < 10}
                          className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
                        >
                          {actionLoading ? 'Declining...' : 'Confirm Decline'}
                        </button>
                        <button
                          onClick={() => setShowDecline(false)}
                          className="px-6 py-2.5 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                </div>
              </div>
            )}

            {/* Already Decided */}
            {humanDecisionMade && (() => {
              const decision = deOutput?.human_decision || research?.human_decision;
              const comment = deOutput?.human_decision_comment || research?.human_decision_comment;
              const isFollowup = decision === 'schedule_follow_up';
              const isProceed = decision === 'proceed';
              return (
                <div className={`rounded-lg p-4 border ${
                  isProceed ? 'bg-green-50 border-green-200'
                    : isFollowup ? 'bg-orange-50 border-orange-200'
                    : 'bg-red-50 border-red-200'
                }`}>
                  <p className="text-sm font-medium">
                    Decision: {isProceed ? 'Approved - Proposal generation triggered' : isFollowup ? 'Schedule Follow-up' : 'Declined'}
                    {comment && <span> - "{comment}"</span>}
                  </p>
                </div>
              );
            })()}
          </div>
        )}

        {/* Failed State */}
        {deOutput && deOutput.status === 'failed' && (
          <div className="bg-white rounded-lg border border-red-200 p-8 text-center">
            <h3 className="text-lg font-semibold text-red-700 mb-2">Analysis Failed</h3>
            <p className="text-gray-500 mb-4">An error occurred during the AI analysis. You can retry.</p>
            <button
              onClick={handleRetrigger}
              disabled={actionLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {actionLoading ? 'Triggering...' : 'Retry Analysis'}
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DecisionEngineReviewPage;
